import React, { useState } from "react";
import { FiClock } from "react-icons/fi";
import Dots from '../../../assets/images/threedot.svg';
import editicons from '../../../assets/images/mage_edit.svg';
import trashicons from '../../../assets/images/delete-rounded.svg';
import RescheduleIcon from '../../../assets/images/Reschedule.svg';
import UserAvatar from "./UserAvatar";

const ClockIcon =
    FiClock as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

interface EventCardProps {
    title?: string;
    startTime?: string;
    endTime?: string;
    attendees?: string[];
    color?: string;
    onEdit?: () => void;
    onDelete?: () => void;
    onReschedule?: () => void;
}

const EventCard: React.FC<EventCardProps> = ({
    title = "-",
    startTime = "",
    endTime = "",
    attendees = [],
    color = "#FF8904",
    onEdit,
    onDelete,
    onReschedule,
}) => {
    const [showActions, setShowActions] = useState(false);

    const getInitials = (name: string) => {
        if (!name) return "?";

        const clean = name.trim().replace(/\s+/g, " ");
        const words = clean.split(" ");

        // ID / random string → only first letter
        if (words.length === 1) {
            return clean.charAt(0).toUpperCase();
        }

        // Normal name → first letter of first two words
        return (words[0][0] + words[1][0]).toUpperCase();
    };

    const colors = [
        "bg-red-500",
        "bg-green-500",
        "bg-blue-500",
        "bg-purple-500",
        "bg-yellow-500",
        "bg-pink-500",
        "bg-indigo-500",
    ];

    const getColor = (name: string) =>
        colors[name.charCodeAt(0) % colors.length];

    const Avatar = ({ name }: { name: string }) => {
        const [show, setShow] = React.useState(false);

        return (
            <div
                className="relative"
                onMouseEnter={() => setShow(true)}
                onMouseLeave={() => setShow(false)}
            >
                <div
                    className={`h-7 w-7 rounded-full border-2 border-white 
            ${getColor(name)} flex items-center justify-center 
            text-xs text-white`}
                >
                    {getInitials(name)}
                </div>

                {show && (
                    <div
                        className="absolute z-[9999] -top-9 left-[100%] 
              -translate-x-1/2 whitespace-nowrap rounded 
              bg-white px-2 py-1 text-xs text-black truncate max-w-24 shadow-md"
                    >
                        {name}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div
            className="bg-white rounded-xl px-3 py-2 flex gap-4 border-l-[3px] cursor-pointer relative"
            style={{ borderColor: color }}
        >
            <div className="flex-1">
                <div className="flex items-start justify-between gap-x-2 w-full">
                    <h2 className="!text-base text-[#101828] truncate w-[75%]">
                        {title.length > 22 ? title.slice(0, 22) + "..." : title}
                    </h2>
                    {/* Three dots menu */}
                    <div className="relative w-[25%] flex justify-end">
                        <img
                            src={Dots}
                            alt="threedot"
                            className="w-5 h-5"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setShowActions(!showActions);
                            }}
                        />
                        {showActions && (
                            <div
                                className="dots_dropdown !w-36 p-0 shadow-md absolute right-0 mt-2 bg-white rounded-md overflow-hidden z-10"
                                style={{ minWidth: '120px' }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                {onReschedule && (
                                    <div
                                        className="dots_dropdown_flx px-3 py-2 w-full text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                                        onClick={() => {
                                            onReschedule();
                                            setShowActions(false);
                                        }}
                                    >
                                        <img src={RescheduleIcon} alt="Reschedule" className="w-4 h-4" />
                                        <span>Reschedule</span>
                                    </div>
                                )}
                                {onEdit && (
                                    <div
                                        className="dots_dropdown_flx px-3 py-2 w-full text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                                        onClick={() => {
                                            onEdit();
                                            setShowActions(false);
                                        }}
                                    >
                                        <img src={editicons} alt="Edit" className="w-4 h-4" />
                                        <span>Edit</span>
                                    </div>
                                )}
                                {onDelete && (
                                    <div
                                        className="dots_dropdown_flx px-3 py-2 w-full text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer flex items-center gap-2"
                                        onClick={() => {
                                            onDelete();
                                            setShowActions(false);
                                        }}
                                    >
                                        <img src={trashicons} alt="Delete" className="w-4 h-4" />
                                        <span>Delete</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2 text-[#4A5565] mt-2">
                    <ClockIcon className="text-[#4A5565]" width={18} height={18} />
                    <span className="text-sm">
                        {startTime} - {endTime}
                    </span>
                </div>

                <div className="flex items-center flex-wrap gap-y-2 mt-1.5">
                    <div className="flex -space-x-1">
                        {attendees.map((name, index) => (
                            <div key={index}>
                                <UserAvatar userId={name} className="w-6 h-6" />
                            </div>
                        ))}
                    </div>

                    <span className="ml-2 text-[#4A5565] text-sm truncate">
                        {attendees.length} Attendees
                    </span>
                </div>

            </div>
        </div>
    );
};

export default EventCard;